import React, { useEffect, useMemo, useState } from "react";
import { BarChart } from "../../common/ReusableComponent/Chart";
import {
  TrendingUp,
  DollarSign,
  Users,
  FileText,
  CalendarDays,
  RefreshCw,
  AlertCircle,
  BarChart3,
  Clock,
  CheckCircle2,
} from "lucide-react";
import useApi from "../../../utils/api-manager/Helper/useApi";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeNumber(amount));

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

const getPercentage = (part, total) => {
  const p = safeNumber(part);
  const t = safeNumber(total);

  if (!t) return 0;

  return Math.min(100, Math.max(0, Math.round((p / t) * 100)));
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`chqdb-kpi-card chqdb-tone-${tone}`}>
    <div className="chqdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="chqdb-kpi-content">
      <div className="chqdb-kpi-label">{label}</div>
      <div className="chqdb-kpi-value">{value}</div>
      {helper && <div className="chqdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue", progress }) => (
  <div className={`chqdb-insight-card chqdb-tone-${tone}`}>
    <div className="chqdb-insight-top">
      <div className="chqdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="chqdb-insight-value">{value}</div>
    {typeof progress === "number" && (
      <div className="chqdb-progress">
        <span style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="chqdb-section-header">
    <div className="chqdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const StatusPill = ({ children, tone = "blue" }) => (
  <span className={`chqdb-pill chqdb-pill-${tone}`}>{children}</span>
);

const EmptyState = ({ message }) => (
  <div className="chqdb-empty-state">
    <FileText size={28} />
    <span>{message}</span>
  </div>
);

const DataTable = ({ columns, rows, emptyLabel }) => (
  <div className="chqdb-table-wrap">
    {rows.length === 0 ? (
      <EmptyState message={emptyLabel} />
    ) : (
      <table className="chqdb-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function ChequeDashboard() {
  const { Post } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [monthlyData, setMonthlyData] = useState({ labels: [], counts: [] });
  const [topIssuers, setTopIssuers] = useState([]);
  const [topByAmount, setTopByAmount] = useState([]);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchChequeDashboard();
  }, []);

  const fetchChequeDashboard = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await Post("chequeDashboardReport", {
        start_date: `${currentYear}-01-01`,
        end_date: `${currentYear}-12-31`,
      });

      const dashboardResponse = response?.data || {};
      const allMonths = Array.from(
        { length: 12 },
        (_, index) => `${currentYear}-${String(index + 1).padStart(2, "0")}-01`
      );

      const labels = allMonths.map((month) =>
        new Date(month).toLocaleString("en-IN", {
          month: "short",
          year: "2-digit",
        })
      );

      const counts = allMonths.map((month) => {
        const found = (dashboardResponse.monthly_data || []).find((entry) =>
          entry.month?.startsWith(month.substring(0, 7))
        );

        return found ? safeNumber(found.count) : 0;
      });

      setMonthlyData({ labels, counts });
      setTopIssuers(Array.isArray(dashboardResponse.top_cheque_issuers) ? dashboardResponse.top_cheque_issuers : []);
      setTopByAmount(Array.isArray(dashboardResponse.top_cheques_by_amount) ? dashboardResponse.top_cheques_by_amount : []);
    } catch (fetchError) {
      console.error("Error fetching cheque dashboard:", fetchError);
      setError(fetchError?.message || "Failed to fetch cheque dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const totalCheques = useMemo(
    () => (monthlyData.counts || []).reduce((sum, count) => sum + safeNumber(count), 0),
    [monthlyData.counts]
  );

  const totalIssuerAmount = useMemo(
    () =>
      topIssuers.reduce(
        (sum, issuer) => sum + safeNumber(issuer.total_amount),
        0
      ),
    [topIssuers]
  );

  const topChequeAmount = safeNumber(topByAmount[0]?.amount);
  const activeVendors = topIssuers.length;
  const averageChequeAmount = totalCheques > 0 ? totalIssuerAmount / totalCheques : 0;
  const highestChequeShare = getPercentage(topChequeAmount, totalIssuerAmount);

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const hasData = totalCheques > 0 || topIssuers.length > 0 || topByAmount.length > 0;

  if (loading) {
    return (
      <div className="responsive-box chqdb-page">
        <style>{dashboardStyles}</style>
        <div className="chqdb-loading">
          <div className="chqdb-spinner" />
          <div>
            <h3>Loading Cheque Dashboard</h3>
            <p>Preparing cheque, issuer and payment insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="responsive-box chqdb-page">
        <style>{dashboardStyles}</style>
        <div className="chqdb-empty-page chqdb-error-page">
          <AlertCircle size={38} />
          <h2>Unable to Load Dashboard</h2>
          <p>{error}</p>
          <button type="button" onClick={fetchChequeDashboard} className="chqdb-retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="responsive-box chqdb-page">
        <style>{dashboardStyles}</style>
        <div className="chqdb-empty-page">
          <FileText size={38} />
          <h2>No Cheque Data Found</h2>
          <p>Print or issue cheques to start viewing cheque dashboard insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box chqdb-page">
      <style>{dashboardStyles}</style>

      <div className="chqdb-shell">
        <section className="chqdb-hero">
          <div>
            <div className="chqdb-kicker">
              <span className="chqdb-kicker-dot" />
              Cheque Business View
            </div>
            <h1>Cheque Dashboard</h1>
            <p>
              Simple executive view for cheque volume, issuer value, highest cheque amount and monthly printing trend.
            </p>
            <div className="chqdb-date-pill">
              <CalendarDays size={14} />
              {currentDateLabel}
            </div>
          </div>

          <div className="chqdb-hero-kpis">
            <KpiCard
              icon={FileText}
              label="Total Cheques"
              value={formatCount(totalCheques)}
              helper={`Printed / issued in ${currentYear}`}
              tone="blue"
            />
            <KpiCard
              icon={Users}
              label="Active Issuers"
              value={formatCount(activeVendors)}
              helper="Vendors appearing in top issuer list"
              tone="purple"
            />
            <KpiCard
              icon={DollarSign}
              label="Highest Cheque"
              value={formatCurrency(topChequeAmount)}
              helper="Highest single cheque amount"
              tone="green"
            />
          </div>
        </section>

        <section className="chqdb-insight-grid">
          <InsightCard
            icon={TrendingUp}
            title="Top Issuers Total"
            value={formatCurrency(totalIssuerAmount)}
            note="Total amount from top cheque issuers"
            tone="green"
          />
          <InsightCard
            icon={Clock}
            title="Average Cheque"
            value={formatCurrency(averageChequeAmount)}
            note="Estimated average based on yearly cheque count"
            tone="blue"
          />
          <InsightCard
            icon={CheckCircle2}
            title="Highest Cheque Share"
            value={`${highestChequeShare}%`}
            note="Highest cheque value against top issuer total"
            progress={highestChequeShare}
            tone="purple"
          />
        </section>

        <section className="chqdb-panel">
          <SectionHeader
            icon={BarChart3}
            title="Cheques Printed Per Month"
            subtitle={`Monthly cheque volume across ${currentYear}`}
          />

          {totalCheques === 0 ? (
            <EmptyState message="No monthly cheque data available." />
          ) : (
            <div className="chqdb-chart-box">
              <BarChart
                labels={monthlyData.labels || []}
                datasets={[
                  {
                    label: "Cheques Printed",
                    data: monthlyData.counts || [],
                    backgroundColor: "#2563eb",
                    borderColor: "#ffffff",
                    borderWidth: 2,
                  },
                ]}
                height="100%"
                legendPosition="top"
                noBorder
                noShadow
                noMargin
              />
            </div>
          )}
        </section>

        <section className="chqdb-table-grid">
          <div className="chqdb-panel">
            <SectionHeader
              icon={Users}
              title="Top Cheque Issuers"
              subtitle="Vendors with highest cheque activity"
            />

            <DataTable
              rows={topIssuers}
              emptyLabel="No issuer data available."
              columns={[
                {
                  key: "name",
                  label: "Vendor",
                  render: (issuer) => <strong className="chqdb-table-title">{issuer.name || "N/A"}</strong>,
                },
                {
                  key: "total_amount",
                  label: "Total Amount",
                  render: (issuer) => <span className="chqdb-money">{formatCurrency(issuer.total_amount)}</span>,
                },
                {
                  key: "date_range",
                  label: "Date Range",
                  render: (issuer) => (
                    <span className="chqdb-muted">
                      {formatDate(issuer.first_cheque_date)} – {formatDate(issuer.last_cheque_date)}
                    </span>
                  ),
                },
              ]}
            />
          </div>

          <div className="chqdb-panel">
            <SectionHeader
              icon={DollarSign}
              title="Highest Cheques by Amount"
              subtitle="Largest single cheque payments"
            />

            <DataTable
              rows={topByAmount}
              emptyLabel="No cheque amount data available."
              columns={[
                {
                  key: "name",
                  label: "Vendor",
                  render: (cheque) => <strong className="chqdb-table-title">{cheque.name || "N/A"}</strong>,
                },
                {
                  key: "amount",
                  label: "Amount",
                  render: (cheque) => <StatusPill tone="blue">{formatCurrency(cheque.amount)}</StatusPill>,
                },
                {
                  key: "issued_date",
                  label: "Date Issued",
                  render: (cheque) => <span className="chqdb-muted">{formatDate(cheque.issued_date)}</span>,
                },
              ]}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .chqdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .chqdb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .chqdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #075985 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .chqdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.16);
  }

  .chqdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .chqdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #e0f2fe;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .chqdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .chqdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .chqdb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .chqdb-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bfdbfe;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .chqdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .chqdb-kpi-card,
  .chqdb-insight-card,
  .chqdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .chqdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .chqdb-kpi-icon,
  .chqdb-insight-icon,
  .chqdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--chqdb-accent);
    background: var(--chqdb-soft);
    border: 1px solid var(--chqdb-border);
    flex-shrink: 0;
  }

  .chqdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .chqdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .chqdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .chqdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .chqdb-insight-grid,
  .chqdb-table-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .chqdb-insight-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .chqdb-table-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chqdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .chqdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .chqdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .chqdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .chqdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .chqdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 26px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .chqdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
  }

  .chqdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .chqdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --chqdb-accent: #0f172a;
    --chqdb-soft: #f8fafc;
    --chqdb-border: #dbe3ec;
  }

  .chqdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .chqdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .chqdb-chart-box {
    height: 290px;
    min-height: 290px;
  }

  .chqdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .chqdb-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--chqdb-accent);
  }

  .chqdb-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .chqdb-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 7px;
    min-width: 620px;
  }

  .chqdb-table th {
    padding: 10px 12px;
    color: #64748b;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 11px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .chqdb-table th:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .chqdb-table th:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .chqdb-table td {
    padding: 11px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 13px;
    font-weight: 750;
    vertical-align: middle;
  }

  .chqdb-table td:first-child {
    border-left: 1px solid #e2e8f0;
    border-radius: 13px 0 0 13px;
  }

  .chqdb-table td:last-child {
    border-right: 1px solid #e2e8f0;
    border-radius: 0 13px 13px 0;
  }

  .chqdb-table-title {
    color: #0f172a;
    font-weight: 950;
  }

  .chqdb-money {
    color: #059669;
    font-weight: 950;
  }

  .chqdb-muted {
    color: #64748b;
  }

  .chqdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .chqdb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .chqdb-empty-state {
    min-height: 190px;
    border: 1px dashed #cbd5e1;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
    background: #f8fafc;
  }

  .chqdb-empty-page {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .chqdb-empty-page svg {
    color: #2563eb;
    margin-bottom: 14px;
  }

  .chqdb-error-page svg {
    color: #dc2626;
  }

  .chqdb-empty-page h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .chqdb-empty-page p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .chqdb-retry-btn {
    margin-top: 14px;
    border: none;
    border-radius: 999px;
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #075985;
    color: #ffffff;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(7, 89, 133, 0.20);
  }

  .chqdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .chqdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .chqdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .chqdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dbeafe;
    border-top-color: #2563eb;
    animation: chqdb-spin 0.8s linear infinite;
  }

  @keyframes chqdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .chqdb-tone-blue {
    --chqdb-accent: #2563eb;
    --chqdb-soft: #eff6ff;
    --chqdb-border: #bfdbfe;
  }

  .chqdb-tone-green {
    --chqdb-accent: #059669;
    --chqdb-soft: #ecfdf5;
    --chqdb-border: #a7f3d0;
  }

  .chqdb-tone-purple {
    --chqdb-accent: #7c3aed;
    --chqdb-soft: #f3e8ff;
    --chqdb-border: #ddd6fe;
  }

  @media (max-width: 1200px) {
    .chqdb-hero {
      grid-template-columns: 1fr;
    }

    .chqdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .chqdb-table-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .chqdb-page {
      padding: 8px 8px 22px;
    }

    .chqdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .chqdb-hero h1 {
      font-size: 25px;
    }

    .chqdb-insight-grid {
      grid-template-columns: 1fr;
    }

    .chqdb-panel,
    .chqdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .chqdb-chart-box {
      height: 280px;
      min-height: 280px;
    }
  }
`;
